import type { MutationUpdaterFn } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import nonEmpty from '../utils/nonEmpty';
import { gql } from '../graphql/generated/gql';
import type {
  HideSidebarItemsMutation,
  ReorderFavoriteIdsMutation,
  ReorderMyTopicIdsMutation,
} from '../graphql/generated/graphql';

const SIDEBAR_SETTINGS_QUERY = gql(`
  query SidebarSettings {
    session: currentSession {
      user {
        id
        settings {
          sidebar {
            favoriteIds
            myTopicIds
            hiddenItems
          }
        }
      }
    }
  }
`);

export const updateSidebarSettingsCache: MutationUpdaterFn<
  | HideSidebarItemsMutation
  | ReorderMyTopicIdsMutation
  | ReorderFavoriteIdsMutation
> = (proxy, { data }) => {
  const sessionData = proxy.readQuery({
    query: SIDEBAR_SETTINGS_QUERY,
  });

  if (!sessionData || !data) {
    return;
  }

  proxy.writeQuery({
    query: SIDEBAR_SETTINGS_QUERY,
    data: {
      session: {
        ...sessionData?.session,
        user: {
          ...sessionData?.session?.user,
          settings: {
            ...sessionData?.session?.user?.settings,
            sidebar: {
              favoriteIds: [],
              myTopicIds: [],
              hiddenItems: [],
              ...sessionData?.session?.user?.settings?.sidebar,
              ...data?.updateUserSettings?.sidebar,
            },
          },
        },
      },
    },
  });
};

const useSidebarSettings = () => {
  const { data, refetch } = useQuery(SIDEBAR_SETTINGS_QUERY);

  const sidebar = data?.session?.user?.settings?.sidebar ?? null;

  return useMemo(() => {
    return {
      favoriteIds: sidebar?.favoriteIds?.filter(nonEmpty) ?? [],
      myTopicIds: sidebar?.myTopicIds?.filter(nonEmpty) ?? [],
      hiddenItems: new Set(sidebar?.hiddenItems?.filter(nonEmpty) ?? []),
      refetch,
    };
  }, [sidebar, refetch]);
};

export default useSidebarSettings;
